import {
  Button,
  Container,
  MantineProvider,
  Title,
  v8CssVariablesResolver,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";

import { webConfig } from "../config.js";
import App from "./App.jsx";
import { AuthProvider } from "./services/context.jsx";
import { queryClient } from "./services/react-query.js";
import { ActionButtonsProvider } from "./services/ActionButtonsContext.jsx";
import { LanguageProvider } from "./services/LanguageContext.jsx";
import { HeaderTitleProvider } from "./services/HeaderTitleContext.jsx";
import theme from "./theme.js";

// Skip Sentry and Posthog initialization in development mode
if (!webConfig.isLocal) {
  Sentry.init({
    dsn: webConfig.sentry.dsn,
    // Events go through our API (apps/api/src/sentryTunnel), because ad
    // blockers drop requests to sentry.io.
    tunnel: `${webConfig.apiUrl}/client-relay`,
    environment: webConfig.environment,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
      }),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring. The API samples its own side of each request
    // separately (apps/api/src/instrument.js).
    tracesSampleRate: webConfig.isProduction ? 0.1 : 0,

    // Session Replay — only sessions that hit an error. PostHog already
    // records ordinary sessions.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  if (webConfig.posthog.key) {
    posthog.init(webConfig.posthog.key, {
      api_host: webConfig.posthog.host,
      autocapture: true,
      enable_heatmaps: true,
      // Ticket links carry the ticket code, a credential: keep it out of
      // every URL PostHog records.
      sanitize_properties: (properties) => {
        for (const key of ["$current_url", "$pathname", "$referrer"]) {
          if (typeof properties[key] === "string") {
            properties[key] = redactTicketCodes(properties[key]);
          }
        }
        return properties;
      },
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
    });
    const posthogEnvironment =
      { development: "preview", production: "prod" }[webConfig.environment] ??
      webConfig.environment;
    posthog.register({ environment: posthogEnvironment });
  }
}

// "/open/claim/ABCD2345" and "/join/ABCD2345" -> ".../[code]".
function redactTicketCodes(url) {
  return url.replace(/\/(open\/claim|join|claim)\/[^/?#]+/gi, "/$1/[code]");
}

const SentryErrorBoundary = Sentry.withErrorBoundary(
  ({ children }) => children,
  {
    fallback: (error, resetError) => {
      console.error(error);
      return (
        <Container>
          <Title order={1}>An unexpected error has occurred</Title>
          <Button onClick={resetError}>Refresh</Button>
        </Container>
      );
    },
  }
);

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={webConfig.OAuth.webClientId}>
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <MantineProvider
            theme={theme}
            cssVariablesResolver={v8CssVariablesResolver}
          >
            <Notifications pauseResetOnHover="notification" />
            <ModalsProvider>
              <AuthProvider>
                <HeaderTitleProvider>
                  <ActionButtonsProvider>
                    <SentryErrorBoundary>
                      <RouterProvider router={App} />
                    </SentryErrorBoundary>
                  </ActionButtonsProvider>
                </HeaderTitleProvider>
              </AuthProvider>
            </ModalsProvider>
          </MantineProvider>
        </QueryClientProvider>
      </LanguageProvider>
    </GoogleOAuthProvider>
  </React.StrictMode>
);
